/// <reference path="../../lib/app.d.ts" />

import {cacheImages} from "../lib/imagecache";
import {EmojiConverter} from "../lib/emoji-converter";
import {DialogViewController} from "./dialog";
import {NextRoomLayer} from "./nextroomtimer";
import {GiftLayer, GiftManager, GiftPanel} from "../component/gift";
import {LoadingLayer} from "./loading";
import {TopupManager, TopupPanel} from "../component/topup";
import {StreamPanel} from "./stream";
import {StateButtons} from "./statebuttons";
import {SpecialGiftPanel} from "./special-gift-panel";
import {CreditPromo} from "./credit-promo";
import {TipAfterShowPanel} from "../component/tip-after-show";
import {AuthPanel} from "../component/auth";
import {ChatMessage, ChatPanel} from "./chat-panel";
import {StaticNoise} from "./static-noise";
import {StreamNotificationManager} from "./stream-notification";
import {WelcomeLayer} from "./welcome-layer";
import {Translator} from "../utils/translator";
import {TemplateEngine} from "../lib/template-engine";
import {HoverControls} from "./hover-controls";
import {ModeControls} from "./mode-controls";
import {ToastManager} from "./toast-manager";
import {SamplerPanel} from "./sampler-panel";
import {VolumeSlider} from "./volume-slider";
import {Collection} from "../lib/event-interface";
import {Application} from "../application/application";
import {UiParams} from "../application/interfaces";
import {FullScreen} from "./utils";
import {ReceiverInterface} from "../receiver/receiver";
import {Storage} from "../lib/storage";
import {CamToCamBase} from "../cam2cam/cam2cam-base";
import * as cam2cam from "../cam2cam/cam2cam";
import logging from "../utils/log";
import {BalanceWatcher} from "../component/balance-watcher";
import {FixedBottom} from "./fixed-bottom";

interface UiCallbacks
{
	onMessageEntered : (message : string) => void;
	onReady : Function;
	onStickerSent : (stickerID : string) => void;
	onVolumeChanged : (vol : number, muted : boolean) => void;
	onJoinRoomRequested : (roomID : string) => void;
}

export class ChatClientUI
{
	balanceWatcher: BalanceWatcher;
	firstHoverWelcomeIgnored: boolean;
	TAG = "[Sxc.Chat.ChatClientUI]";

	private ready      : boolean;
	private uiPrepared : boolean;

	streamRatio   : number;
	streamRatioID : string;

	mouseMoveEventTimer : number;

	/** Previous app size */
	prevSize : string;

	/** Current app size */
	size : string;

	private $root          : JQuery;
	private $bottomPanel   : JQuery;
	private $extraBottomPanel   : JQuery;
	private $tipAfterShowPanel   : JQuery;
	private $authPanel   : JQuery;
	private $samplerPanel  : JQuery;
	private $chat          : JQuery;
	private $wrapper       : JQuery;
	private $streamWrapper : JQuery;

	private emoji : EmojiConverter;

	private fixedBottom: FixedBottom = null;

	// Components

	public dialog            : DialogViewController;

	public nextRoomLayer     : NextRoomLayer;
	public giftLayer         : GiftLayer;
	public loadLayer         : LoadingLayer;

	public cam2cam           : CamToCamBase;
	public giftPanel         : GiftPanel;
	public topupPanel        : TopupPanel;
	public streamPanel       : StreamPanel;
	public bottomPanel       : StateButtons;
	public specialGiftPanel  : SpecialGiftPanel;
	public creditPromo       : CreditPromo;
	public tipAfterShowPanel  : TipAfterShowPanel;
	public authPanel  : AuthPanel;
	public chatPanel         : ChatPanel;
	public staticNoise       : StaticNoise;
	public streamNotificationManager: StreamNotificationManager;

	public welcome : WelcomeLayer;

	// Dependencies
	public translator     : Translator;
	public templateEngine : TemplateEngine;
	public receiver       : ReceiverInterface;
	public storage        : Storage;
	public giftManager    : GiftManager;
	public topupManager   : TopupManager;

	// Callbacks
	callbacks : UiCallbacks;

	// Mess :)
	public hoverControls   : HoverControls;
	public modeControls   : ModeControls;

	public toastManager : ToastManager;
	public samplerPanel : SamplerPanel;
	public volume : VolumeSlider;

	public buttons : Collection;

	constructor (private $container, private app : Application, private params : UiParams, private flavour : string)
	{
		this.buttons = new Collection("buttons");
	}

	initialize()
	{
		this.emoji = new EmojiConverter(this.params.emojiPath);

		this.$root = $("<div class=\"sxc-chat\"></div>")
			.addClass("sxcc-app-loading")
			.addClass("sxcc-flavour-" + this.flavour);

		this.$container.replaceWith(this.$root);

		$.ajax({
			url: this.params.templateFile,
			dataType: "text",
			success: (r) => {
				this.$root.html(r);
				this.$root.append(this.templateEngine.render("root", {
					isMobileFlavour: this.flavour === "mobile",
					isTestingFlavour: this.flavour === "testing",
					isFullFlavour: this.flavour === "full",
					flavour: this.flavour
				}));
				// this.$root.append(this.templateEngine.render("root", {
				// 	isMobileFlavour: false,
				// 	isTestingFlavour: true,
				// 	isFullFlavour: false,
				// 	flavour: "testing"
				// }));

				this.setVisibility(false);
				this.$root.removeClass("sxcc-app-loading");
				this.prepareUI();

				// Set up Securon payment even before joining rooms
				if (this.topupManager && this.app.options.securionPayHandler) {
					this.app.options.securionPayHandler.register(this.topupManager.getSecurionPayEventHandler());
					this.topupManager.setSecurionPayHandler(this.app.options.securionPayHandler);
				}
			}
		});
	}

	onAfterInitialization()
	{
		if (!this.uiPrepared) {
			logging.log(this.TAG, "[onAfterInitialization]", "Template is not yet ready");
			return;
		}

		if (!this.receiver || !this.receiver.isInitialized()) {
			logging.log(this.TAG, "[onAfterInitialization]", "Receiver is not yet ready");
			return;
		}

		let storedVolume = this.storage.get("defaultVolume", 50);
		let storedVolumeMuted = this.storage.get("defaultVolumeMuted", "");
		this.receiver.setVolume(storedVolumeMuted ? 0 : storedVolume);

		this.ready = true;

		if (this.callbacks.onReady) {
			this.callbacks.onReady();
		}
	}

	private prepareUI()
	{
		logging.log(this.TAG, "Preparing user interface...");

		$('.sxcc-stream-wrapper').on('mousemove', function() {
			if (this.mouseMoveEventTimer !== null) {
				$('.sxcc-ctrl, .sxcc-close-chat, .sxcc-welcome-layer').css({'opacity': ''});
				$('.sxcc-stream-cover').css({'cursor': 'default'});

				clearTimeout(this.mouseMoveEventTimer);
			}

			this.mouseMoveEventTimer = setTimeout(() => {
				$('.sxcc-ctrl, .sxcc-close-chat, .sxcc-welcome-layer').css({'opacity': '0'});
				$('.sxcc-stream-cover').css({'cursor': 'none'});
			}, 3000);
		});

		// Prepare JQuery elements used directly here...
		this.$wrapper = this.$root.find(".sxcc-wrapper");
		this.$bottomPanel = this.$root.find(".sxcc-bottom");
		this.$extraBottomPanel = this.$root.find(".sxcc-special-gift-panel");
		this.$tipAfterShowPanel = this.$root.find(".sxcc-tip-after-show-wrapper");
		this.$authPanel = this.$root.find(".sxcc-auth-panel-wrapper");
		this.$samplerPanel = this.$root.find(".sxcc-sampler");
		this.$chat = this.$root.find(".sxcc-chat");
		this.$streamWrapper = this.$root.find(".sxcc-stream-wrapper");

		this.nextRoomLayer = new NextRoomLayer(this.$root.find(".sxcc-wait-for-next"));

		this.hoverControls = new HoverControls(this.$streamWrapper.find(".sxcc-ctrl"), this);

		this.streamPanel = new StreamPanel(this.$streamWrapper);
		this.loadLayer = new LoadingLayer(this.$root);

		this.cam2cam = cam2cam.initCam2Cam(
			this.$streamWrapper.find(".sxcc-cam2cam"),
			this.params.cam2cam,
			this.app
		);

		this.bottomPanel = new StateButtons(
			this.$bottomPanel,
			this.$streamWrapper.find(".sxcc-streambutton"),
			this.$bottomPanel.find(".sxccb-actions"),
			this.translator,
			this.app.options.auth.tokenID === "guest",
			this.flavour
		);

		this.specialGiftPanel = new SpecialGiftPanel(
			this.$extraBottomPanel,
			this.templateEngine,
			this.translator,
			this.giftManager
		);

		this.welcome = new WelcomeLayer(this.$streamWrapper.find(".sxcc-welcome-layer"));

		this.authPanel = new AuthPanel(
			this.$authPanel,
			this.app,
			this.templateEngine,
			this.translator,
			this.app.options.userAuth.captchaURL,
			this.app.options.url,

			(user : string, email : string, passwd : string, onReady : (res : string) => void) => {
				this.app.options.userAuth.register(user, email, passwd, onReady);
			},

			(user : string, passwd : string, captcha : string, remember : boolean, onReady : (res : string) => void) => {
				this.app.options.userAuth.login(user, passwd, captcha, remember, onReady);
			},

			(email : string, onReady : (res : string) => void) => {
				this.app.options.userAuth.reminder(email, onReady);
			}
		);

		this.tipAfterShowPanel = new TipAfterShowPanel(
			this.$tipAfterShowPanel,
			this.translator,
			(roomID : string, amount : number, message : string, onResult : (success : boolean, reason : string) => void) =>
			{
				let isGuest = this.app.options.auth.tokenID == "guest";

				if (isGuest) {
					this.app.showRegisterNeeded("tip");
					return;
				}

				let onSuccess = () => {
					// Note: giftNotification event is also received, that should handle chat msg
					onResult(true, null);
				};

				let onFail = (result) =>
				{
					switch (result.failureReason)
					{
						case "ooc":
							// this.app.showOutOfCredit("tip");
							onResult(false, "ooc");
							break;

						default:
							onResult(false, "technical");
					}
				};

				this.app.chatServer.sendGift(roomID, "_tip", message, amount, onSuccess, onFail);
			}
		);

		this.giftLayer = new GiftLayer(this.$streamWrapper, this.giftManager);
		this.giftPanel = new GiftPanel(
			this.$streamWrapper.find(".sxcc-gift-panel"),
			this.templateEngine,
			this.translator,
			this.giftManager,
			this.hoverControls
		);

		this.creditPromo = new CreditPromo(this.$streamWrapper.find(".sxcc-stream-promo"), this.app, this, this.translator);

		// Prepare handler instances for different components...
		this.samplerPanel = new SamplerPanel(this.$samplerPanel, this.app, this.app.options.showBoys);

		this.staticNoise = new StaticNoise(<HTMLCanvasElement> this.$streamWrapper.find("canvas")[0], this.samplerPanel);

		this.streamNotificationManager = new StreamNotificationManager(this.$root.find(".sxcc-stream-notification-wrapper"), this.welcome);

		this.chatPanel = new ChatPanel(this.$chat, this.samplerPanel, this.emoji, this.app);

		this.chatPanel.event.on("stateChange", () =>
		{
			this.toastManager.setSize(
				this.size == "fs" ? "fs" : "normal",
				(this.size == "fs" && this.chatPanel.getOpened()) ? 450 : 0);

			this.streamNotificationManager.setSize(
				this.size == "fs" ? "fs" : "normal",
				(this.size == "fs" && this.chatPanel.getOpened()) ? 450 : 0);

			this.storage.set("chatPanelOpened", this.chatPanel.getOpened());
			this.chatCamToCamStateChange();
		});

		if (this.cam2cam) {
			// in the off chance that cam2cam is not supported
			this.cam2cam.event.on("stateChange", (state) => {
				this.chatCamToCamStateChange();
				this.hoverControls.toggleCamToCam(state !== cam2cam.Status.idle);
			});
		}

		this.chatPanel.onMessageEntered = this.callbacks.onMessageEntered;
		this.chatPanel.onStickerSent = this.callbacks.onStickerSent;

		let chatPanelOpened = this.storage.get("chatPanelOpened", false);

		this.volume = new VolumeSlider(
			this.$root.find(".sxcc-volume-control"),
			this,
			this.callbacks.onVolumeChanged
		);

		let storedVolume = this.storage.get("defaultVolume", 50);
		let storedVolumeMuted = this.storage.get("defaultVolumeMuted", "");
		this.volume.setVolume(storedVolume ? storedVolume : 50, !!storedVolumeMuted);

		this.toastManager = new ToastManager(this.$root.find(".sxcc-toast-wrapper"), this.welcome, this.streamNotificationManager);

		this.modeControls = new ModeControls(
			this.$streamWrapper.find(".sxcc-mode"),
			this.toastManager,
			this.app.translator,
			this.topupManager);

		if (this.topupManager) {
			this.balanceWatcher = new BalanceWatcher(this.app);
			this.balanceWatcher.setEnabled(!this.topupManager.isAutoPayEnabled());
		}

		this.dialog = new DialogViewController(this.$root, this.templateEngine);

		this.chatPanel.setToggleable(true);
		this.chatPanel.setResizable(false);
		this.chatPanel.setOpened(chatPanelOpened);

		if (this.topupManager)
		{
			this.topupManager.setOnQuickBuyChange((enabled : boolean) => {
				this.modeControls.setQbEnabled(enabled);
				this.modeControls.update();
			});

			this.topupManager.setOnAutoPayChange((enabled : boolean) =>
			{
				this.balanceWatcher.setEnabled(!enabled);
				if (!enabled) {
					this.balanceWatcher.hideWarn();
				}
			});

			this.topupManager.setOnPromoChange((promo) => {
				this.modeControls.updatePromo(promo);
			});
			this.modeControls.updatePromo(this.topupManager.getCurentPromo());

			this.topupPanel = new TopupPanel(
				this.$streamWrapper.find(".sxcc-topup-panel"),
				this.templateEngine,
				this.topupManager,
				this.toastManager,
				this.translator,
				this,
				this.storage
			);

			this.modeControls.setQbEnabled(
				this.topupManager.isQuickBuyEnabled()
			);
		}

		FullScreen.onChange((isFullscreen) =>
		{
			if (!isFullscreen && this.size == "fs") {
				logging.log(this.TAG, "[FullScreen] exit");
				this.setSize(this.prevSize);
			}
		});

		// Initialize view...

		this.setRatio("169");

		this.setSize(this.storage.get("uiSize", this.params.defaultSize));

		// Set and trigger window resize
		this.onWindowResize();
		$(window).on("resize", this.onWindowResize.bind(this));

		this.$root.on("click", "[data-action]", this.actionButtonHandler.bind(this));

		let fontSize = parseInt(this.storage.get("chatBoxFontSize", 0), 10);
		if (fontSize) this.chatPanel.setFontSize(fontSize);

		this.uiPrepared = true;

		// Initialize the receiver if it's ready. If not (template is prepared first)
		// the setReceiver method also does a similar check and initialization
		if (this.receiver && !this.receiver.isInitialized()) {
			this.receiver.initialize(this.$root.find(".sxcc-obj-wrapper"));
		}

		logging.log(this.TAG, "[prepareUI]", "User interface is prepared!");
		this.onAfterInitialization();

		$(window).on("keyup", e =>
		// this.$root.on("keyup", e =>
		{
			if (!this.isVisible() && !this.escIgnoreClose) return;
			if (e.keyCode !== 27) return;
			if (this.escIgnore) return;
			if(typeof this.app.options.chatIncludeType !== 'undefined' && this.app.options.chatIncludeType.indexOf("babesIFrame") !==-1 && $(".popup:visible").length>0) {return;}

			logging.log(this.TAG, "[esc]", "Esc pressed");

			if (this.authPanel.isVisible()) {
				logging.log(this.TAG, "[esc]", "Auth Panel is visible, delegating call...");
				this.authPanel.hide();
				return;
			}

			if (this.tipAfterShowPanel.isVisible()) {
				logging.log(this.TAG, "[esc]", "TipAfterChat is visible, delegating call...");
				this.tipAfterShowPanel.hide();
				return;
			}

			if (this.dialog.isVisible()) {
				logging.log(this.TAG, "[esc]", "Dialog is visible, delegating call...");
				this.dialog.esc();
				return;
			}

			if (this.giftPanel.isVisible()) {
				logging.log(this.TAG, "[esc]", "Giftpanel is visible, delegating call...");
				this.giftPanel.esc();
				return;
			} else {
				logging.log(this.TAG, "[esc]", "Gift panel is NOT visible");
			}

			if (this.topupPanel && this.topupPanel.isVisible()) {
				logging.log(this.TAG, "[esc]", "Topup panel is visible, closing...");
				this.topupPanel.esc();
				return;
			} else {
				logging.log(this.TAG, "[esc]", "Topup panel is NOT visible");
			}

			if (this.chatPanel.isStickersVisible()) {
				logging.log(this.TAG, "[esc]", "Stickers are visible, closing...");
				this.chatPanel.cancelStickers();
				return;
			} else {
				logging.log(this.TAG, "[esc]", "Stickers are NOT visible");
			}

			if (this.cam2cam && this.cam2cam.isSettingsVisible()) {
				this.cam2cam.toggleSettings(false);
				return;
			}

			if (this.escIgnoreClose) return;

			logging.log(this.TAG, "[esc]", "Nothing else needs to be closed, closing app...");
			this.app.close();

			$.each($('video'), function(index, videoObj) { if(typeof $(videoObj).attr('id') !== "undefined") { let videoDOM : HTMLVideoElement = <HTMLVideoElement>document.getElementById($(videoObj).attr('id')); videoDOM.pause(); videoDOM.src = ""; } } );
		});
	}

	private actionButtonHandler(e)
	{
		let $btn = $(e.currentTarget);
		let action = $btn.data("action");

		logging.log("Action button clicked: ", action);

		this.buttons.trigger(action, $btn);
	}

	setReceiver(receiver : ReceiverInterface)
	{
		//if (this.receiver) throw this.TAG + " Receiver is already set.";

		this.receiver = receiver;

		//if (this.uiPrepared && !this.receiver.isInitialized()) {
		//	logging.log(this.TAG, "[setReceiver] Initializing receiver");
		//	this.receiver.initialize(this.$root.find(".sxcc-obj-wrapper"));
		//}

		if(this.ready != true) {
			this.onAfterInitialization();
		}
	}

	isReady()
	{
		return this.ready;
	}

	addMessage(message : ChatMessage, forceJump : boolean = false)
	{
		this.chatPanel.addMessage(message, forceJump);
	}

	setRatio(ratio)
	{
		this.streamRatioID = ratio;
		this.streamRatio = ratio == "169" ? 16/9 : 4/3;

		logging.log(this.TAG, "[setRatio]", this.streamRatioID, this.streamRatio);

		this.$root
			.removeClass("sxcc-ratio-169 sxcc-ratio-43")
			.addClass("sxcc-ratio-" + ratio);

		this.onWindowResize();
	}

	setSize(size)
	{
		logging.log(this.TAG, "setSize:", size);

		if (size == "_next") {
			if (this.size === "normal") {
				this.setSize("fb");
				return;
			} else if (this.size === "fb") {
				this.setSize("fs");
				return;
			} else {
				this.setSize("normal");
				return;
			}
		}

		this.hoverControls.setSize(size);

		if (this.size === size) return;

		this.$root
			.removeClass("sxcc-size-fs sxcc-size-fb sxcc-size-normal")
			.addClass("sxcc-size-" + size);

		if (size === "fs") FullScreen.request(this.$root[0]);
		else if (this.size === "fs" ) FullScreen.cancel();

		this.prevSize = this.size;
		this.size = size;

		this.onWindowResize();
		this.chatCamToCamStateChange();
	}

	public onWindowResize()
	{
		if (!this.isVisible()) return;

		let size;
		if (this.flavour === "full") {
			size = this.onWindowResizeNormal();
		}
		else if (this.flavour === "mobile") {
			// size = this.onWindowResizeMobile();
		}
		this.app.event.trigger("sizeChanged", size.w, size.h, size.left, size.top);
	}

	/**
	 * This method calculates the new size of some components of the app. These components are resized based on
	 * some tricky rules, so it's more simple to do this here than in CSS.
	 */
	public onWindowResizeNormal()
	{
		const $win = $(window);

		let ww = this.$wrapper.width();
		let wh = $win.height();

		if(this.fixedBottom !== null) { this.fixedBottom.setChatRatio(this.streamRatioID); this.fixedBottom.setChatSize(this.size); }

		if (this.size == "fs")
		{
			// In fullscreen mode things are easy, everything is controlled by css, so we clear positioning
			this.streamPanel.clearPlace();
			this.bottomPanel.clearPlace();
			this.specialGiftPanel.clearPlace();

			// Also: no sampler in fullscreen mode :)
			if (this.samplerPanel) this.samplerPanel.show(false);

			this.chatPanel.setToggleable(true);
			this.chatPanel.setResizable(true);

			// Maybe we can remove this as this is already initialized with width()
			ww = this.$wrapper.innerWidth();

			this.chatPanel.setToggleable(true);
			this.chatPanel.setResizable(true);
			let chatPanelW = 450;
			this.chatPanel.place(ww - chatPanelW - 10, 10, chatPanelW, wh - 20);
			if (this.cam2cam) this.cam2cam.setFixedWidth(chatPanelW);
			this.toastManager.setSize("fs", this.chatPanel.getOpened() ? chatPanelW : 0);

			this.$root["removeClass"]("sxcc-mini");
		}
		else
		{
			// Not fullscreen. All calculations are the same regardless of which size we are in.
			// Normal mode is just full browser with css controlled paddings. So that's why
			// we use the innerwidth of the wrapper instead of the real width of the window

			if(typeof this.app.options.chatIncludeType != 'undefined' && this.app.options.chatIncludeType == 'babesIFrame') {
				if(typeof this.app.options.chatForceWindowHeightFS != 'undefined') {
					wh = this.app.options.chatForceWindowHeightFS;
				}
			}

			var bottomHeight = this.$bottomPanel.outerHeight();
			var bottom2Height = this.$extraBottomPanel.outerHeight();

			// Some params to be used with the calculations
			var samplerMinWidth = Math.max(280, ww * 0.2);
			var samplerMaxWidth = Math.max(280, ww * 0.25);
			var wrapperMinHeight = 500;

			// Need to force a minimum height in order to avoid unreasonably small chat
			if (wh < wrapperMinHeight) wh = wrapperMinHeight;

			// The size of the stream is easy: Defined by the remaining height above the bottom panel and
			// the aspect ratio of the stream.
			var streamHeight = wh - bottomHeight - bottom2Height;
			var streamWidth = streamHeight * this.streamRatio;

			//if(typeof this.app.options.chatIncludeType != 'undefined' && this.app.options.chatIncludeType == 'babesIFrame') {
			//	streamWidth = Number(($('.sxcc-wrapper').width() / 4) * 3);
			//	streamHeight = streamWidth / this.streamRatio;
			//}

			// If this would be to big we recalc based on the width minus the min width of the sampler.
			if (streamWidth > (ww - samplerMinWidth)) {
				streamWidth = ww - samplerMinWidth;
				streamHeight = Math.floor(streamWidth / this.streamRatio);
				wh = streamHeight + bottomHeight + bottom2Height;
			}

			this.streamPanel.place(0, 0, streamWidth, streamHeight);
			this.bottomPanel.place(0, Math.floor(streamHeight), streamWidth, null);
			this.specialGiftPanel.place(0, Math.floor(streamHeight) + bottomHeight + 5, ww, null);

			// Now we place the sampler and the chat. The remaining space next to the chat is divided up
			// between them, if it's big enough. If it's , the chat is positioned above the sampler

			let samplerWidth, chatWidth, chatRight;

			if (ww - streamWidth > samplerMaxWidth)
			{
				// Sampler is visible, there is too much space

				// Acually let' see how big the sampler would be, and see if there is enough items to fill it

				let avail = ww - streamWidth;
				let samplerSize = avail / 3;

				const samplerVisible = this.samplerPanel.bigList.length * samplerSize * (3 / 4)  > wh - bottom2Height;

				this.chatPanel.setToggleable(false);
				this.chatPanel.setResizable(false);

				// If the sampler should be visible we place it next to the chat. If not, we place it under the
				// chat (some widgets expect it to be present)

				if (samplerVisible) {

					this.samplerPanel.show();

					this.samplerPanel.place(
						streamWidth + avail / 3 * 2,
						0,
						avail / 3,
						wh - bottom2Height
					);
					this.chatPanel.place(
						streamWidth,
						0,
						avail / 3 * 2,
						wh - bottom2Height);
				} else {

					this.samplerPanel.show(false);

					this.samplerPanel.place(
						streamWidth + avail / 3 * 2,
						0,
						avail,
						wh - bottom2Height
					);
					this.chatPanel.place(
						streamWidth,
						0,
						avail,
						wh - bottom2Height);

				}
			} else {
				// Chat overlaps the sampler
				// If we aren't in fullscreen we still don't want it to be resizable

				const samplerSize = ww - streamWidth;
				const samplerVisible = this.samplerPanel.bigList.length * samplerSize * (3 / 4) > wh - bottom2Height;

				if (samplerVisible) {
					this.samplerPanel.show(true);
					this.chatPanel.setToggleable(true);
					this.chatPanel.setResizable(false);
				} else {
					this.samplerPanel.show(false);
					this.chatPanel.setToggleable(false);
					this.chatPanel.setResizable(false);
				}

				if (this.samplerPanel) this.samplerPanel.place(
					streamWidth,
					0,
					ww - streamWidth,
					wh - bottomHeight - bottom2Height
				);
				this.chatPanel.place(
					streamWidth,
					0,
					ww - streamWidth,
					wh - bottom2Height
				);
			}
			this.toastManager.setSize("normal", 0);
			this.$root[streamHeight < 500 ? "addClass" : "removeClass"]("sxcc-mini");
		}

		wh = Math.min($win.innerHeight(), wh);
		this.$wrapper.css("height", wh);

		// Trigger resize handlers of other components
		this.giftLayer.resize();
		if (this.cam2cam) this.cam2cam.setFixedWidth(null); // This will also trigger reflow

		setTimeout(() => this.chatPanel.scrollDown(() => {}, true), 1);

		if (this.topupPanel) {
			this.topupPanel.onResize();
		}

		if(this.size == 'normal' || this.size == 'fb') {
			let streamBottom = Math.round($('.sxcc-stream').outerHeight(true)) + Math.round($('.sxccb-actions').outerHeight(true)) + 8 + 157;
			if($(".sxcc-special-gift-panel").css("visibility")=="hidden"){streamBottom=streamBottom-$(".sxcc-special-gift-panel").height();}
			$('.sxcc-user-data').css({top: streamBottom+'px'}).show();
		} else if(this.size == 'fs') {
			$('.sxcc-user-data').hide();
		}

		let left = this.$wrapper.offset().left;
		let top = this.$wrapper.offset().top;

		return {w: ww, h: wh, left, top};
	}

	isVisible() : boolean
	{
		return this.$root && this.$root.hasClass("sxcc-app-visible");
		// return this.isBackDropVisible() && this.isAppVisible();
	}

	isAppVisible() : boolean
	{
		return this.$root
			? this.isVisible()
			// ? this.$root.find(".sxcc-wrapper").hasClass("sxcc-visible")
			: false;
	}

	isBackDropVisible() : boolean
	{
		return this.$root.hasClass("sxcc-app-visible");
	}

	private wasVisible = false;

	setVisibility(visible : boolean)
	{
		logging.log(this.TAG, "[setVisibility]", visible);

		this.app.event.trigger("backdrop-visible", visible);
		this.app.event.trigger("app-visible", visible);
		this.app.event.trigger("visible", visible);

		this.$root[visible ? "addClass" : "removeClass"]("sxcc-app-visible");

		let tmp = window.getComputedStyle(this.$root[0]).top;
		this.onWindowResize();

		this.firstHoverWelcomeIgnored = false;

		if (visible && !this.wasVisible)
		{
			this.wasVisible = true;
			cacheImages(this.giftManager.getList().map(g => g.image), 10000, 60000);
		}

		requestAnimationFrame(() => this.onWindowResize());

		// if (this.isVisible() === visible) return;
		// logging.log(this.TAG, "[setVisibility]", visible);
		//
		// this.setBackDropVisibility(visible);
		// this.setAppVisibility(visible);
		//
		// this.app.event.trigger("visible", visible);
		//
		// if (visible && !this.wasVisible)
		// {
		// 	this.wasVisible = true;
		// 	Sxc.cacheImages(this.giftManager.getList().map(g => g.image), 10000, 60000);
		// }

		if (visible) {
			if (this.topupManager && this.app.options.securionPayHandler) {
				this.app.options.securionPayHandler.register(this.topupManager.getSecurionPayEventHandler());
				this.topupManager.setSecurionPayHandler(this.app.options.securionPayHandler);
			}

			this.app.updateECHH(true);

			this.$root.find(".sxcc-close-chat").addClass("sxc-force-visible");

			setTimeout(() => {
				this.$root.find(".sxcc-close-chat").removeClass("sxc-force-visible");
			}, 10000);

			//if(typeof this.app.options.chatIncludeType == 'undefined') {
			//	$(() => { this.fixedBottom = new FixedBottom(); this.fixedBottom.setChatRatio(this.streamRatioID); this.fixedBottom.setChatSize(this.size); });
			//}
		} else {
			$('.sxcc-fixed-bottom').empty();
			$('.sxcc-fixed-bottom-handler').hide();

			if (this.topupManager && this.app.options.securionPayHandler) {
				this.app.options.securionPayHandler.unRegister();
			}
		}
	}

	destroy():void
	{
		logging.log(this.TAG, "[destroy]");
		this.$root.remove();
	}

	setScreenName(screenName:string):void
	{
		this.$root.find("[data-bind=screenname]").text(screenName);
	}

	showFlashMissing():void
	{
		// this.setBackDropVisibility(true);
		this.setVisibility(true);
		let $root = this.$root.find(".sxcc-no-flash");

		$root.addClass("sxc-visible");

		let ua = navigator.userAgent;

		let setActiveView = (view : string) => {
			$root.find(".chrome, .chrome2, .normal").hide();
			$root.find("." + view).show();
			$root.find(".arrow")[view == "chrome2" ? "show" : "hide"]();
		};

		if(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(ua)) {
			setActiveView("normal");
		} else if(/Chrome/i.test(ua)) {
			setActiveView("chrome");

			if (/Edge/i.test(ua)) {
				$root.find(".arrow").addClass("right");
			}

			$root.find("a").click(() => {
				setActiveView("chrome2");
			});

		} else {
			setActiveView("normal");
		}

	}

	private cancelDisconnectedAnim = false;

	showError(visible : boolean = true, error : string = null) : void
	{
		logging.log(this.TAG, "[showError]", visible);

		let $box = this.$root.find(".sxcc-error");
		if (error) $box.find(".sxcc-error-desc").html(error);

		if (visible)
		{
			$box.addClass("sxc-visible");
			this.staticNoise.start();
		}
		else
		{
			$box.removeClass("sxc-visible");
			this.staticNoise.cancel();
		}
	}

	private currentContent : string;
	private currentContentHandler : Function;

	/**
	 * Shows a content block by rendering it's template, and initializing the provided handler (if any)
	 */
	showContent(content : string, handler : any = null, params = null) : void
	{
		if (this.currentContent) this.clearContent();

		logging.log(this.TAG, "content", "show", content, params);

		var $t = this.templateEngine.render("content-" + content, params);
		this.$root.find(".sxcc-content-over-stream")
			.empty()
			.addClass("sxcc-content-" + content)
			.addClass("sxc-visible")
			.append($t);

		this.currentContent = content;
		this.currentContentHandler = handler;

		if (handler && "initialize" in handler) handler["initialize"](this.$root);
	}

	clearContent()
	{
		logging.log(this.TAG, "content", "clear", this.currentContent);
		if (!this.currentContent) return;

		this.$root.find(".sxcc-content-over-stream")
			.empty()
			.removeClass("sxcc-content-" + this.currentContent)
			.removeClass("sxc-visible");

		if (this.currentContentHandler && "destroy" in this.currentContentHandler) {
			// @ts-ignore
			this.currentContentHandler["destroy"]();
		}

		this.currentContent = null;
		this.currentContentHandler = null;
	}

	private chatCamToCamStateChange()
	{
		if (!this.cam2cam) return;
		let opened = this.chatPanel.getOpened();
		let toggleable = this.chatPanel.getToggleable();
		let c2cStatus = this.cam2cam.getStatus();

		let c2cStreaming = (
			c2cStatus == cam2cam.Status.streamingMini ||
			c2cStatus == cam2cam.Status.streaming ||
			c2cStatus == cam2cam.Status.prepareStream);

		let topSpace = this.$streamWrapper.height() - this.chatPanel.getHeight();
		if (this.size === "fs") topSpace -= 20; // 10px at each side

		let visibleClose = "normal";

		if (this.size === "fs")
		{
			if (c2cStreaming) {
				visibleClose = null
			} else if (topSpace < 40) {
				visibleClose = "hover";
			}

			if (topSpace < this.cam2cam.getSize().h + 10) {
				this.cam2cam.shiftLeft(456);
			} else {
				this.cam2cam.shiftLeft(0);
			}
		}
		else
		{
			if (opened && !toggleable && !this.samplerPanel.isVisible()) {
				visibleClose = "hover";
			} else if (opened && toggleable) {
				visibleClose = "hover";
			}

			if (c2cStreaming && visibleClose == "hover") {
				visibleClose = null
			}
			this.cam2cam.shiftLeft(0);
		}

		// let action = visibleClose == "normal" ? "addClass" : "removeClass";
		// this.$root.find(".sxcc-close-chat")[action]("sxc-visible");
		// this.hoverControls.showClose(visibleClose == "hover");
	}

	showCovers(b: boolean)
	{
		this.$root.find(".sxcc-content-over-stream")[b ? "removeClass" : "addClass"]("sxc-force-invisible");
	}

	escIgnore : boolean = false;
	escIgnoreClose : boolean = false;

	ignoreEsc(b: boolean)
	{
		this.escIgnore = b;
	}

	ignoreEscClose(b: boolean)
	{
		this.escIgnoreClose = b;
	}
}
